import { BackIcon } from "@minket/brand/icons/back";
import { cn } from "@minket/ui/lib/utils";
import { Link } from "@tanstack/react-router";
import {
  createContext,
  lazy,
  type ReactNode,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { tabTitle } from "@/lib/navigation";
import { usePlace } from "@/lib/place";
import { FitText } from "./fit-text";

const SectionTabs = lazy(() =>
  import("./section-tabs.ondemand").then((module) => ({ default: module.SectionTabs })),
);
const loadEngraving = () => import("./engraving.ondemand");
const Engraving = lazy(() => loadEngraving().then((module) => ({ default: module.Engraving })));

/**
 * Sets `<title>` ("Emails · Minket") and, unless `remember` is false, records the page for the
 * palette's recent group under the same title. The record loads on its own (it changes nothing
 * on screen), so the first paint does not wait for it.
 */
export function useDocumentTitle(title: string | false, remember = true) {
  useEffect(() => {
    if (title === false) return;
    document.title = `${title} · Minket`;
    if (!remember) return;
    const path = window.location.pathname;
    void import("@/components/shell/recent").then((recent) => recent.rememberPage(path, title));
  }, [title, remember]);
}

/** Only detail pages show a serial, so it (and its copy button) loads with them. */
const Serial = lazy(() => import("./serial").then((module) => ({ default: module.Serial })));

const line = "max-w-[72ch] text-sm text-muted-foreground wrap-break-word";
const backLink =
  "mb-2 inline-flex w-fit items-center gap-1 rounded-sm text-[13.5px] whitespace-nowrap text-muted-foreground transition-colors duration-150 ease-out-quiet hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none";

/**
 * Every page opens with its head (direction §7, bolder for the owner). The band: the title in
 * the wide face at full width, the engraved line running on from it (engraving.tsx; it drops
 * under a long title rather than shrink below 120 px), the page's one primary action at the end
 * of that row, an object's serial under its title, and the head's rule in plate ink with the
 * section's tabs standing on it. Under the rule, the page's own sentence, when it has one. The
 * band is the section's, so moving between its tabs never moves it; the sentence and action are
 * the tab's.
 *
 * On each tab of a section with more than one (lib/navigation.ts) the title is the section's
 * name and `title` names the document only ("Received · Emails"). Titles keep one line: a long
 * name narrows and takes one size down (FitText); a name no line can hold (an email's subject,
 * a long name on a phone) is set on balanced lines instead, never cut. A test-mode page (the
 * sandbox) is a `specimen`: the overprint on its band, never behind the sentence, and
 * `stamp={<TestModeStamp />}` beside the title (direction §8.9).
 */
export function Page({
  title,
  documentTitle,
  description,
  serial,
  trace,
  action,
  back,
  tabs,
  specimen = false,
  stamp,
  remember = true,
  standalone = false,
  children,
  className,
}: {
  title: ReactNode;
  /**
   * The <title> text; required when `title` is not a plain string on a page that is not a
   * section's tab. `false` leaves the document title to a child (setup steps name themselves).
   */
  documentTitle?: string | false;
  /** One sentence under the rule; it may hold a lamp beside its words. */
  description?: ReactNode;
  /** The object's id, under its title: `{ id, label: "domain id" }`. One line, with Copy. */
  serial?: { id: string; label: string };
  /**
   * A series the page holds (the overview's sends per day): the head's engraved line swells with
   * it, so the line carries the section's own record (engraving-trace.tsx).
   */
  trace?: readonly number[];
  action?: ReactNode;
  /** A link back to the list this page belongs to. */
  back?: { to: string; label: string };
  /** Tabs of the page's own, in place of its section's. */
  tabs?: ReactNode;
  specimen?: boolean;
  /** A stamp beside the title: `TestModeStamp`, a mode badge. */
  stamp?: ReactNode;
  /** False keeps the page out of the palette's recent pages (the not-found page). */
  remember?: boolean;
  /** A page outside the console's sections (the gallery): its own title, no section tabs. */
  standalone?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const place = usePlace();
  const frame = useContext(SectionFrameContext);
  const section = standalone ? null : place.section;
  const here = standalone ? null : place.here;
  const tabbed = here && here.section.tabs.length > 1 ? here.section : null;
  const named =
    documentTitle ??
    (here ? tabTitle(here.section, here.tab) : typeof title === "string" ? title : "Console");
  useDocumentTitle(named, remember);

  // Inside a tabbed section the shell draws the head once (SectionFrame), so switching tabs
  // never remounts it and this page never draws a second one: it sends its sentence, action and
  // stamp into the head's slots, and its series and specimen state up to the head.
  const framed = frame !== null && tabbed !== null;
  const traceKey = trace?.join(",") ?? "";
  const setHead = frame?.setHead;
  useEffect(() => {
    if (!framed || !setHead) return;
    setHead({ trace: traceKey === "" ? undefined : traceKey.split(",").map(Number), specimen });
    return () => setHead({ trace: undefined, specimen: false });
  }, [framed, setHead, traceKey, specimen]);
  if (frame && framed) {
    return (
      <div className={cn("grid min-w-0 content-start gap-6", className)}>
        {description && frame.description
          ? createPortal(<div className={line}>{description}</div>, frame.description)
          : null}
        {action && frame.action ? createPortal(action, frame.action) : null}
        {stamp && frame.stamp ? createPortal(stamp, frame.stamp) : null}
        {children}
      </div>
    );
  }

  const heading = tabbed ? tabbed.label : title;
  const sectionTabs =
    tabs ??
    (tabbed ? (
      <Suspense fallback={null}>
        <SectionTabs section={tabbed} />
      </Suspense>
    ) : null);

  return (
    <div className={cn("grid min-w-0 content-start gap-6", className)}>
      <PageHead
        heading={heading}
        engraving={section?.label ?? (typeof title === "string" ? title : "Minket")}
        back={back}
        stamp={stamp}
        specimen={specimen}
        trace={trace}
        serial={serial}
        tabs={sectionTabs}
        sentence={description ? <div className={line}>{description}</div> : null}
        action={action ?? null}
      />
      {children}
    </div>
  );
}

/** What a tab sends its section's head: slots for its sentence, action and stamp; its series. */
interface FrameSlots {
  description: HTMLElement | null;
  action: HTMLElement | null;
  stamp: HTMLElement | null;
  setHead: (head: { trace: readonly number[] | undefined; specimen: boolean }) => void;
}

const SectionFrameContext = createContext<FrameSlots | null>(null);

/**
 * The head of a section with tabs, drawn once by the shell around the routed page: the title,
 * the engraved line and the tab bar stay mounted while the reader moves between tabs, and only
 * the content below changes (the owner, 2026-10-29: a tab switch used to rebuild the whole head).
 * The current page renders its sentence and action into the head's two slots (`Page`). `tabs`
 * gives a section its own tab bar (the admin portal's, with the open ticket count).
 */
export function SectionFrame({
  tabs,
  children,
}: {
  tabs?: Partial<Record<string, ReactNode>>;
  children: ReactNode;
}) {
  const { here } = usePlace();
  const [description, setDescription] = useState<HTMLElement | null>(null);
  const [action, setAction] = useState<HTMLElement | null>(null);
  const [stamp, setStamp] = useState<HTMLElement | null>(null);
  const [head, setHead] = useState<{ trace: readonly number[] | undefined; specimen: boolean }>({
    trace: undefined,
    specimen: false,
  });
  const value = useMemo(
    () => ({ description, action, stamp, setHead }),
    [description, action, stamp],
  );
  const tabbed = here && here.section.tabs.length > 1 ? here.section : null;
  if (!tabbed) return <>{children}</>;
  return (
    <SectionFrameContext.Provider value={value}>
      <div className="grid min-w-0 content-start gap-6">
        <PageHead
          heading={tabbed.label}
          engraving={tabbed.label}
          tabs={
            tabs?.[tabbed.key] ?? (
              <Suspense fallback={null}>
                <SectionTabs section={tabbed} />
              </Suspense>
            )
          }
          trace={head.trace}
          specimen={head.specimen}
          stamp={<div ref={setStamp} className="contents" />}
          sentence={<div ref={setDescription} data-frame-slot="" className="contents" />}
          action={<div ref={setAction} data-frame-slot="" className="contents" />}
          slots
        />
        {children}
      </div>
    </SectionFrameContext.Provider>
  );
}

/** A frame's slot shows only once the page has put something in it (SectionFrame). */
const emptySlotHidden = "[&:not(:has([data-frame-slot]>*))]:hidden";

/**
 * The band every page opens with. The action ends the title row: one line, after the engraved
 * line, dropping under the title when the row has no room, so it never stands alone under the
 * rule. `slots` keeps the action and sentence wrappers for portalled content.
 */
function PageHead({
  heading,
  engraving,
  back,
  stamp,
  specimen = false,
  trace,
  serial,
  tabs,
  sentence,
  action,
  slots = false,
}: {
  heading: ReactNode;
  engraving: string;
  back?: { to: string; label: string };
  stamp?: ReactNode;
  specimen?: boolean;
  trace?: readonly number[];
  serial?: { id: string; label: string };
  tabs: ReactNode;
  sentence: ReactNode;
  action: ReactNode;
  slots?: boolean;
}) {
  return (
    <header data-slot="page-head" className="grid min-w-0">
      <div className={cn("grid min-w-0", specimen && "overprint -mx-4 px-4 pt-4 md:-mx-8 md:px-8")}>
        {back ? (
          <Link to={back.to} className={backLink}>
            <BackIcon size={16} className="-ml-1" />
            {back.label}
          </Link>
        ) : null}
        <div className="@container/head flex min-w-0 flex-wrap items-center gap-x-5 gap-y-2">
          <h1 className="type-title min-w-0 text-[28px] md:text-[34px] xl:text-[38px]">
            <FitText wrap>{heading}</FitText>
          </h1>
          {stamp ? <div className="shrink-0">{stamp}</div> : null}
          <Suspense
            fallback={
              <span
                aria-hidden
                className="block min-w-[7.5rem] flex-1 basis-[7.5rem]"
                style={{ height: 18 }}
              />
            }
          >
            <Engraving
              name={engraving}
              trace={trace}
              height={18}
              className="min-w-[7.5rem] flex-1 basis-[7.5rem]"
            />
          </Suspense>
          {action ? (
            // Shares the line when the title, the line's 120 px and the action all fit (the line
            // takes what is left); else it starts the next line under the title. Alone and still
            // too wide (several buttons on a phone), its buttons wrap among themselves, each on
            // one line. On a phone it follows the serial, so an object's id stays under its name.
            // Beside the title it is one button high, whatever hangs under a button (a reason it
            // is dimmed, "All changes saved"), so the band keeps its height from tab to tab.
            <div
              data-slot="page-action"
              className={cn(
                "flex max-w-full min-w-0 flex-wrap items-start gap-2 empty:hidden @max-[36rem]/head:order-2 @min-[36rem]/head:h-9",
                slots && emptySlotHidden,
              )}
            >
              {action}
            </div>
          ) : null}
          {serial ? (
            <div className="order-1 -mt-1 min-h-6 min-w-0 basis-full">
              <Suspense fallback={null}>
                <Serial id={serial.id} label={serial.label} />
              </Suspense>
            </div>
          ) : null}
        </div>
        <div
          className={cn("min-w-0 border-b border-seal-ink/45", tabs || serial ? "mt-3" : "mt-4")}
        >
          {tabs}
        </div>
      </div>
      {sentence ? (
        <div className={cn("mt-4 min-w-0", slots && emptySlotHidden)}>{sentence}</div>
      ) : null}
    </header>
  );
}

/**
 * A settings section's page (`settings/*.tsx`): since the nine sections, a page like any other,
 * whose head is its section's (the title, the tabs) with this page's sentence and action, and
 * its own title as the outline's h2 (visually its tab), so its sections stay h3.
 */
export function SubPage({
  title,
  description,
  action,
  tabs,
  children,
}: {
  title: string;
  /** No longer read: the section and the tab name the document ("Organization · Settings"). */
  parent?: string;
  description?: string;
  action?: ReactNode;
  tabs?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Page title={title} description={description} action={action} tabs={tabs}>
      {/* The tab names the page on screen; this names it in the outline, above its h3 sections. */}
      <h2 className="sr-only">{title}</h2>
      {children}
    </Page>
  );
}
